/**
 * Button — GoodGuardian design system
 *
 * Variants: primary | secondary | ghost | danger
 * Sizes:    sm | md | lg
 */
export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-semibold rounded-xl ' +
    'transition-all duration-200 tap-target select-none focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95'

  const variants = {
    primary:
      'brand-gradient text-white shadow-md hover:opacity-90 focus-visible:ring-brand-500',
    secondary:
      'bg-brand-100 text-brand-700 hover:bg-brand-200 focus-visible:ring-brand-500',
    ghost:
      'bg-transparent text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500',
    danger:
      'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
    outline:
      'border-2 border-brand-700 text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-slate-800',
  }

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-5 py-3.5 text-base',
    lg: 'px-7 py-4 text-lg',
  }

  const disabledCls = disabled || loading ? 'opacity-50 cursor-not-allowed active:scale-100' : 'cursor-pointer'

  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      className={[
        base,
        variants[variant],
        sizes[size],
        fullWidth ? 'w-full' : '',
        disabledCls,
        className,
      ].join(' ')}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        Icon && iconPosition === 'left' && <Icon className="w-4 h-4 flex-shrink-0" />
      )}
      {children}
      {!loading && Icon && iconPosition === 'right' && (
        <Icon className="w-4 h-4 flex-shrink-0" />
      )}
    </button>
  )
}
